import * as React from 'react'
import styled from '@emotion/styled'
import GroupedButton from '../../atoms/GroupButton'
import { useState, useRef, useEffect } from 'react'
import { useWindowSize } from 'react-use'
// import { motion } from 'framer-motion'
import { Category } from '@type/main'
import { useCategoriesState } from '@contexts/categoriesContext'
import { useRouter } from 'next/router'
import GroupedButtonsLoading from '@components/organisms/Skeletons/GroupedButtonsLoading'
import ArrowDown from '@public/static/icons/referrals/arrowDown'
import { getLocalizedCategoryName } from '@utils/getLocalizedCategoryName'

const GroupedButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: flex-start;
  height: 60px;
  //cross browser commands to hide scrollbar, but allow scrolling
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  gap: 1.45rem;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  overflow-y: hidden;
  position: relative;
  ::-webkit-scrollbar {
    width: 0;
    display: none;
    background: transparent; /* make scrollbar transparent Chrome */
  }
  @media screen and (max-width: 640px) {
    height: auto;
    min-height: 40px;
    width: 100%;
    background-color: white;
    padding: 8px 12px;
    gap: 0.25rem;
  }
`
const GroupedButtonsContainerWrapper = styled.div`
  width: 100%;
`
const HorizontalScrollButton = styled.button`
  background-color: #ffffff;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #eee;
  box-sizing: border-box;
  transition: transform 0.175s cubic-bezier(0.645, 0.045, 0.355, 1);
  will-change: transform;
  backface-visibility: hidden;
  :active {
    border: 1px solid #eee;
    transform: scale(1) !important;
  }
  :hover {
    transform: scale(1.25);
  }
`

const GroupedButtons = (props: {
  setCategory?: React.Dispatch<React.SetStateAction<string>>
  category: string
}) => {
  const [activeButton, setActiveButton] = useState<number>(-1)
  const [prevButton, setPrevButton] = useState<number>(0)
  const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 })
  const [showBtns, setShowBtns] = useState<boolean>(false)
  const [buttonsLens, setButtonsLens] = useState<number[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [maxScrollValue, setMaxScrollValue] = useState(0)
  const { setCategory } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { categories, isLoading } = useCategoriesState()
  const { width: windowWidth } = useWindowSize()
  const router = useRouter()

  const handleClickRight = () => {
    if (!containerRef.current) return
    containerRef.current.scrollBy({
      top: 0,
      left: 200,
      behavior: 'smooth',
    })
  }
  const handleClickLeft = () => {
    if (!containerRef.current) return
    containerRef.current.scrollBy({
      top: 0,
      left: -200,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const handleScroll = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)

      timeoutRef.current = setTimeout(() => {
        if (!containerRef.current) return
        setScrollOffset({
          x: containerRef.current.scrollLeft,
          y: containerRef.current.scrollTop,
        })
      }, 100)
    }

    const container = containerRef.current
    container?.addEventListener('scroll', handleScroll)

    return () => {
      container?.removeEventListener('scroll', handleScroll)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [containerRef])

  useEffect(() => {
    setShowBtns(windowWidth - 16 != 700)
  }, [windowWidth])

  const calculateMaxScrollValue = () => {
    const containerWidth = containerRef?.current?.clientWidth || 0
    const contentWidth = containerRef?.current?.scrollWidth || 0

    // Set the max scroll value
    setMaxScrollValue(contentWidth - containerWidth)
  }

  useEffect(() => {
    if (buttonsLens.length != categories?.length) setButtonsLens([])

    if (buttonsLens.length == 0) {
      categories?.forEach((element: Category) => {
        const localArray = buttonsLens
        localArray.push(element.name.en?.length * 7.75 + 32)
        setButtonsLens(localArray)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories])

  useEffect(() => {
    if (containerRef?.current && categories?.length > 0) {
      calculateMaxScrollValue()
    }

    const handleResize = () => {
      calculateMaxScrollValue()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [categories])

  return (
    <GroupedButtonsContainerWrapper>
      <GroupedButtonsContainer
        ref={containerRef}
        className="md:snap-x md:snap-mandatory mb-1"
      >
        {isLoading && (
          <div className="flex justify-between w-full min-w-[78rem] items-center mt-2 sm:ml-0 ml-[7.6px]">
            {GroupedButtonsLoading}
          </div>
        )}

        {!isLoading &&
          categories?.map((item: Category, index: number) => {
            const localizedName = getLocalizedCategoryName(
              item.name,
              router.locale
            )
            return (
              localizedName && (
                <GroupedButton
                  key={item._id}
                  active={props.category === item._id}
                  current={activeButton}
                  index={index}
                  prevButton={prevButton}
                  avgWidth={buttonsLens}
                  onClick={() => {
                    if (setCategory) {
                      if (props.category === item._id) {
                        setPrevButton(0)
                        setActiveButton(-1)
                        return 'all'
                      }
                      setPrevButton(activeButton)
                      setActiveButton(index)
                      router.push(
                        `?category=${encodeURIComponent(
                          JSON.stringify([item._id])
                        )}`
                      )
                      // setCategory((prev) => {
                      //   return item._id
                      // })
                    }
                  }}
                >
                  {localizedName}
                </GroupedButton>
              )
            )
          })}
      </GroupedButtonsContainer>
      {/* {!isLoading && (
        <motion.div
          initial={{
            opacity: scrollOffset.x != 0 && showBtns ? 0 : 1,
            scale: 0.5,
          }}
          animate={{
            opacity: scrollOffset.x != 0 && showBtns ? 1 : 0,
            scale: scrollOffset.x != 0 && showBtns ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
          style={{
            background:
              'linear-gradient(90deg, rgba(255,255,255,1) 70%, rgba(0,212,255,0) 96%)',
            position: 'absolute',
            left: 0,
            paddingRight: 20,
          }}
          className="sm:top-[23%] top-[12.5%] -left-2 sm:left-0 sm:inline hidden z-10"
        >
          <HorizontalScrollButton
            onClick={handleClickLeft}
            aria-label="Previous Category"
          >
            <IoIosArrowBack />
          </HorizontalScrollButton>
        </motion.div>
      )}
      {!isLoading && (
        <motion.div
          initial={{
            opacity: 1,
            scale: 0.5,
          }}
          animate={{
            opacity: scrollOffset.x + 50 >= maxScrollValue ? 0 : 1,
            scale: scrollOffset.x + 50 >= maxScrollValue ? 0 : 1,
          }}
          transition={{ duration: 0.2 }}
          style={{
            background:
              'linear-gradient(270deg, rgba(255,255,255,1) 70%, rgba(0,212,255,0) 96%)',
            position: 'absolute',
            paddingLeft: 20,
          }}
          className="sm:top-[23%] top-[12.5%] sm:right-0 right-0.5 sm:inline hidden"
        >
          <HorizontalScrollButton
            onClick={handleClickRight}
            style={{ right: '-10px' }}
            aria-label="Next Category"
          >
            <IoIosArrowForward />
          </HorizontalScrollButton>
        </motion.div>
      )} */}
      {!isLoading && (
        <div
          style={{
            background:
              'linear-gradient(90deg, rgba(255,255,255,1) 70%, rgba(0,212,255,0) 96%)',
            position: 'absolute',
            left: 0,
            paddingRight: 20,
            opacity: scrollOffset.x !== 0 && showBtns ? 1 : 0,
            transform:
              scrollOffset.x !== 0 && showBtns ? 'scale(1)' : 'scale(0)',
            transition: 'opacity 0.3s, transform 0.3s',
          }}
          className="sm:top-[23%] top-[12.5%] -left-2 sm:left-0 sm:inline hidden z-10"
        >
          <HorizontalScrollButton
            onClick={handleClickLeft}
            aria-label="Previous Category"
          >
            {/* <IoIosArrowBack /> */}
            <ArrowDown className="rotate-90" />
          </HorizontalScrollButton>
        </div>
      )}

      {!isLoading && (
        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(255,255,255,1) 70%, rgba(0,212,255,0) 96%)',
            position: 'absolute',
            paddingLeft: 20,
            opacity: scrollOffset.x + 50 >= maxScrollValue ? 0 : 1,
            transform:
              scrollOffset.x + 50 >= maxScrollValue ? 'scale(0)' : 'scale(1)',
            transition: 'opacity 0.2s, transform 0.2s',
          }}
          className="sm:top-[23%] top-[12.5%] sm:right-0 right-0.5 sm:inline hidden"
        >
          <HorizontalScrollButton
            onClick={handleClickRight}
            style={{ right: '-10px' }}
            aria-label="Next Category"
          >
            <ArrowDown className="-rotate-90" />
          </HorizontalScrollButton>
        </div>
      )}
    </GroupedButtonsContainerWrapper>
  )
}

export default GroupedButtons
